/**
 * Users service — group memberships of a user (list/search/add/kick/ban/scope).
 * Ported 1:1 from the legacy flat services/users.js. Shapes verified against
 * docs/users-migration/Api-response/ (user-groups-list.json, group-*.json).
 */
import constants from '../../constants'
import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import {
  ChatApp,
  GroupMembersChangeResponse,
  UserGroupsListResponse,
} from './types'

/**
 * LEGACY ERROR CONTRACT (deliberate): resolves with the raw error object on
 * failure — same reasoning as crud.list (frozen apps/* saga consumers).
 */
export async function groupList(
  id: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
): Promise<UserGroupsListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}/groups?hasJoined=1&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchText}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error)
}

export async function searchGroupList(
  id: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchKey = '',
): Promise<UserGroupsListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}/groups?hasJoined=1&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchKey}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Search within a user's joined groups (add-to-group modal). */
export async function groupSearch(
  id: string,
  app: ChatApp,
  apiHost: string,
  searchText: string,
  pageNo = 1,
): Promise<UserGroupsListResponse> {
  const URL = `${getChatApiBase(
    app,
    apiHost,
  )}/users/${id}/groups?hasJoined=1&searchKey=${searchText}&per_page=${
    constants.perPageInListings
  }&page=${pageNo}`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Result is a per-uid map — a failed add is still an HTTP 200; callers branch
 *  on `data.participants[uid].success` (verified: group-add-member*.json). */
export async function addToGroup(
  uid: string,
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<GroupMembersChangeResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/members`
  return axios
    .post(
      `${URL}`,
      { participants: [uid] },
      {
        headers: getChatApiHeaders(app),
      },
    )
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function kickFromGroup(
  uid: string,
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<GroupMembersChangeResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/members/${uid}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function banFromGroup(
  uid: string,
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<GroupMembersChangeResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/bannedusers/${uid}`
  return axios
    .post(
      `${URL}`,
      {},
      {
        headers: getChatApiHeaders(app),
      },
    )
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Scope change rides the member-add endpoint: the payload key (admins /
 *  moderators / participants) selects the scope. */
export async function updateScopeInGroup(
  uid: string,
  guid: string,
  scope: string,
  app: ChatApp,
  apiHost: string,
): Promise<GroupMembersChangeResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/members`
  let scopeData: Record<string, string[]> = { participants: [uid] }
  if (scope === 'Admin') {
    scopeData = { admins: [uid] }
  } else if (scope === 'Moderator') {
    scopeData = { moderators: [uid] }
  }
  return axios
    .post(`${URL}`, scopeData, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
